import React, { useCallback, useRef, useState } from 'react';
import UserInfoComponent from './UserInfoComponent';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { USER_PROFILE_QUERY } from '../../queries';
import { useMutation } from '@apollo/client';
import { UPDATE_USER_ADDITIONAL_DATA_MUTATION } from 'features/userProfile/mutations';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { USER_ADDITIONAL_DATA_UPDATED } from 'features/userProfile/subscriptions';
import { useToast } from 'utils/hooks';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';

export const UserInfoContainer = () => {
  const [expanded, setExpanded] = useState(true);
  const addToast = useToast();
  const editor = useRef(null);
  const [saving, setSaving] = useState(false);
  const [userInfoLens, dirtyInfo, resetUserInfoLens] = useChangeTrackingLens(null);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  useQueryWithErrorHandling(USER_PROFILE_QUERY, {
    onCompleted: data => {
      resetUserInfoLens(data.userInfo);
    }
  });

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setSaving(false);
    }
  });

  const [updateUserAdditionalData] = useMutation(UPDATE_USER_ADDITIONAL_DATA_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.updateUserAdditionalData;
      storeKey({ correlationId });
    }
  });

  useGlobalSubscription(USER_ADDITIONAL_DATA_UPDATED, {
    onSubscriptionData: () => {
      setSaving(false);
    }
  });

  const handleUpdateUserAdditionalData = useCallback(
    async editor => {
      setSaving(true);

      if (!editor.current) {
        await updateUserAdditionalData({ variables: { input: { userId: userInfoLens.userId |> get, avatar: null } } });
        return;
      }
      const canvas = editor.current.getImage();
      const resizedPhoto = handleResize(canvas);

      await updateUserAdditionalData({
        variables: { input: { userId: userInfoLens.userId |> get, avatar: resizedPhoto } }
      });
    },
    [updateUserAdditionalData, userInfoLens.userId]
  );

  const handleResize = canvas => {
    const offScreenCanvas = document.createElement('canvas');
    const width = 300;
    const height = 300;
    offScreenCanvas.width = width;
    offScreenCanvas.height = height;
    offScreenCanvas.getContext('2d', { willReadFrequently: true }).drawImage(canvas, 0, 0, width, height);
    return offScreenCanvas.toDataURL('image/png', 0.9);
  };

  return (
    <>
      {(userInfoLens |> get) && (
        <UserInfoComponent
          userInfoLens={userInfoLens}
          toggleExpanded={toggleExpanded}
          expanded={expanded}
          onSaveUserInfo={handleUpdateUserAdditionalData}
          editor={editor}
          saving={saving}
          isDirtyInfo={isDirty(dirtyInfo)}
        />
      )}
    </>
  );
};

export default UserInfoContainer;
